import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Loader } from "rsuite";
import { useState, useEffect } from "react";
import { axInst } from "../../../Components/Login/Service";
import WaterMark from "../../../Components/WaterMark/WaterMark.jsx";
import TwoComparisionBarChart from "../../../Components/Charts/TwoComparisionBarChart";

// Custom month order
const customMonthOrder = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

// API to fetch CPSC data
const cpscData = async (body) => {
  try {
    const res = await axInst.post("dealer-review/cpsc_data", body);
    const responseData = res.data;

    if (!Array.isArray(responseData)) {
      console.error("Invalid API response format:", responseData);
      return [];
    }

    return responseData
      .map((item) => {
        const { month, years } = item;
        const sortedYears = Object.keys(years).sort((a, b) => b - a);

        return {
          key: month,
          mtd: { value: years?.[sortedYears[0]] ?? 0 },
          lmtd: { value: years?.[sortedYears[1]] ?? 0 },
          years: sortedYears,
          sortIndex: customMonthOrder.indexOf(month),
        };
      })
      .filter((item) => item.sortIndex !== -1)
      .sort((a, b) => a.sortIndex - b.sortIndex);
  } catch (error) {
    console.error("Error fetching CPSC data:", error);
    return [];
  }
};

// API to fetch dropdown options
const fetchOptions = async (filters) => {
  try {
    const response = await axInst.post("/dealer-review/cpsc_filter", filters);
    return response.data || [];
  } catch (error) {
    console.error("Error fetching dropdown options:", error);
    return [];
  }
};

const Cpsc = ({ productLine: staticProductLine, show = false }) => {
  const { data, submitted } = useSelector((e) => e.filtersDealerReview);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(""); // Default = All Product Line

  // Web view: fetch dropdown options
  useEffect(() => {
    if (staticProductLine) return; // skip for PDF view
    const loadOptions = async () => {
      const dropdownOptions = await fetchOptions(data);
      setOptions(dropdownOptions);

      // Keep default "" (All Product Line), don't auto-select first option
    };
    loadOptions();
  }, [data, staticProductLine]);

  // Final productLine to send
  const effectiveProductLine = staticProductLine || selectedOption;

  // Fetch CPSC data
  const {
    data: stockData = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["cpsc", submitted, effectiveProductLine],
    queryFn: () =>
      cpscData({
        ...data,
        Product_Line: effectiveProductLine === "" ? [] : [effectiveProductLine], // IMPORTANT FIX
      }),
    cacheTime: 0,
    staleTime: 0,
  });

  const safeStockData = Array.isArray(stockData) ? stockData : [];
  const legendData = safeStockData.length
    ? safeStockData[0].years.map((year) => `FY-${year}`)
    : [];

  return (
    <div className="bg-white rounded-md p-2 relative h-full">
      <WaterMark />

      {/* Show dropdown only in Web view */}
      <div className="relative w-full">
        {!staticProductLine && !show && (
          <div className="absolute top-2 right-[40%] z-50">
            <select
              className="border border-gray-300 text-[12px] rounded-md w-36 p-1 bg-white"
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
            >
              <option value="">All Product Line</option>
              {options.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label || opt.key}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isFetching ? (
        <div className="grid items-center justify-center w-full h-full">
          <Loader />
        </div>
      ) : isError || safeStockData.length === 0 ? (
        <div className="grid items-center justify-center w-full h-full">
          <p>Data not available</p>
        </div>
      ) : (
        <TwoComparisionBarChart
          data={safeStockData}
          heading={`CPSC${staticProductLine ? ` - ${staticProductLine}` : ""}`}
          legendData={legendData}
          ChartHeight="220px"
        />
      )}
    </div>
  );
};

export default Cpsc;
